import React from 'react';
import { motion, Reorder } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  Play, 
  Pause, 
  X, 
  GripVertical, 
  FileText,
  Music
} from 'lucide-react';

export function PlaylistItem({ 
  song, 
  index, 
  currentIndex, 
  isPlaying, 
  hasLyrics,
  onSongClick, 
  onSongRemove, 
  onAddLyrics,
  isCompact = false
}) {
  const isCurrentSong = index === currentIndex;
  
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Adjust sizing for compact mode (floating playlist)
  const itemPadding = isCompact ? 'p-1.5' : 'p-2';
  const iconSize = isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3';
  const buttonSize = isCompact ? 'h-5 w-5' : 'h-6 w-6';
  const textSize = isCompact ? 'text-xs' : 'text-sm';
  const subTextSize = isCompact ? 'text-xs' : 'text-xs';

  return (
    <Reorder.Item
      value={song}
      id={song.id}
      className="group playlist-item"
      whileDrag={{ scale: 1.02, zIndex: 10 }}
    >
      <motion.div
        className={`
          flex items-start gap-2 rounded-lg transition-all duration-200
          ${isCurrentSong 
            ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30' 
            : 'bg-white/5 hover:bg-white/10 border border-white/10'
          }
          cursor-pointer ${itemPadding}
        `}
        onClick={(e) => onSongClick(index, e)}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        {/* Left Side - Drag Handle and Play/Pause */}
        <div className="flex items-center gap-2 pt-1">
          {/* Drag Handle */}
          <div className="drag-handle cursor-grab active:cursor-grabbing text-white/40 hover:text-white/60">
            <GripVertical className={iconSize} />
          </div>

          {/* Play/Pause Icon */}
          <div className="flex-shrink-0">
            {isCurrentSong && isPlaying ? (
              <Pause className={`${iconSize} text-blue-400`} />
            ) : (
              <Play className={`${iconSize} text-white/60`} />
            )}
          </div>
        </div>

        {/* Center - Song Info (Dual Line) */}
        <div className="flex-1 min-w-0">
          {/* First Line - Track Title */}
          <h4 className={`${textSize} font-medium leading-tight ${isCurrentSong ? 'text-blue-300' : 'text-white'}`}>
            {song.title || song.name || 'Unknown Track'}
          </h4>
          
          {/* Second Line - Duration with Music Icon */}
          <div className={`flex items-center gap-1 ${subTextSize} text-white/50 mt-1`}>
            <Music className={isCompact ? 'h-2 w-2' : 'h-2.5 w-2.5'} />
            <span>{formatDuration(song.duration)}</span>
          </div>
        </div>

        {/* Right Side - Action Buttons */}
        <div className="flex items-center gap-1 pt-1">
          {/* Single Lyrics Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onAddLyrics(song);
            }}
            className={`
              lyrics-button flex-shrink-0 ${buttonSize} p-0 transition-all duration-200
              ${hasLyrics 
                ? 'text-green-400 hover:text-green-300 hover:bg-green-500/20' 
                : 'text-white/40 hover:text-white/60 hover:bg-white/10'
              }
            `}
            title={hasLyrics ? "Manage lyrics" : "Add lyrics"}
          >
            <FileText className={iconSize} />
          </Button>

          {/* Remove Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onSongRemove(index);
            }}
            className={`remove-button flex-shrink-0 ${buttonSize} p-0 text-white/40 hover:text-red-400 hover:bg-red-500/20 transition-all duration-200`}
          >
            <X className={iconSize} />
          </Button>
        </div>
      </motion.div>
    </Reorder.Item>
  );
}